import { findGetFilter, findGetParameter, findAllGetParameters } from '../utils/parameters';
import { PINNED_STORAGE_KEY } from '../constants';
import type {
  IAttribute,
  IBookmarkGroup,
  IFilterAttributes,
  IItem,
  IParam,
  IParams,
} from '../types';
import { getMyBeckhoffService } from '../../mybeckhoff/mybeckhoff';

type ItemFilter = { [key: string]: string[] };
type DownloadFilter = { [key: string]: number };
type Selected = { [depth: number]: number };

export interface IContextState {
  expandedCard?: string;
  filterAttributes: IFilterAttributes;
  params: IParams;
  itemFilter?: ItemFilter;
  countedCategories: Array<number>;
  attributes: Array<IAttribute>;
  selected: Selected;
  page: number;
  selectionAmount: number;
  showToast: boolean;
  message: string;
  bookmarks: Array<any>;
  term?: string;
  totalDownloads: number;
  shareIds: number[];
  preFilter: Record<string, any> | null;
  results?: Array<any>;
  productPageResults?: Array<any>;
  loadMore?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  zipSize: number;
  expanded?: number | string | null;
  zipItems: number;
  selectedBookmarks?: Array<number>;
  bookmarkData?: Array<IBookmarkGroup>;
  bookmarkItems?: Array<IItem>;
  loggedIn?: boolean;
  availableFilters?: {
    dfAttributeDfFileType: DownloadFilter;
    dfAttributeDfLanguage: DownloadFilter;
    variants: DownloadFilter;
  };
}

const getInitialStateFromUrl = (): {
  itemFilter: ItemFilter;
  term: string;
  shareIds: number[];
  params: IParams;
  selected: Selected;
} => {
  const shareIds = findAllGetParameters('download_group');
  const filterParams = findGetParameter('filter');
  const paramArray = ['c-1', 'c-2', 'c-3', 'c-4', 'c-5', 'c-6'];
  const params = paramArray
    .map((item) => findGetFilter(item))
    .filter((param): param is IParam => !!param);
  const selectedFilter = params.reduce(
    (previousValue, nextValue) => ({
      ...previousValue,
      [nextValue.depth]: nextValue.pimId,
    }),
    {},
  );
  return {
    term: findGetParameter('search'),
    itemFilter: filterParams ? JSON.parse(filterParams) : {},
    params,
    selected: selectedFilter,
    shareIds: shareIds.map((shareId) => parseInt(shareId, 10)),
  };
};

const getInitialBookmarkState = () => {
  const storageValue = window.localStorage.getItem(PINNED_STORAGE_KEY);

  let storedBookmarks = [];
  if (storageValue) {
    try {
      storedBookmarks = JSON.parse(storageValue);
    } catch (e) {
      console.error((e as Error)?.message);
    }
  }

  const sharedBookmarks = findGetParameter('bookmarks');

  if (sharedBookmarks) {
    const sharedItems = sharedBookmarks.split(',').map((share) => {
      const value = share.split(':');
      return {
        id: parseInt(value[1], 10),
        group: parseInt(value[0], 10),
      };
    });

    const uniqueSet = new Set();
    storedBookmarks = storedBookmarks
      .concat(sharedItems)
      .filter((val: { id: number; group: number }) => {
        const stringifidObject = JSON.stringify(val);
        const duplicate = uniqueSet.has(stringifidObject);
        uniqueSet.add(stringifidObject);
        return !duplicate;
      });
    window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(storedBookmarks));
  }

  return { bookmarks: storedBookmarks };
};

const getInitialLoginStatus = () => {
  const myBeckhoffService = getMyBeckhoffService();
  return { loggedIn: myBeckhoffService.isLoggedIn };
};

export const initialState: IContextState = {
  expandedCard: undefined,
  filterAttributes: {
    dfAttributeDfFileType: {},
    dfAttributeDfLanguage: {},
    filterAttributeGroup: undefined,
  },
  countedCategories: [],
  params: [],
  attributes: [],
  selected: {},
  itemFilter: {},
  page: 1,
  selectionAmount: 0,
  totalDownloads: 0,
  shareIds: [],
  showToast: false,
  message: '',
  bookmarks: [],
  zipSize: 0,
  zipItems: 0,
  selectedBookmarks: [],
  expanded: null,
  bookmarkData: [],
  bookmarkItems: [],
  preFilter: null,
};

export const initialResultsState: IContextState = {
  ...initialState,
  ...getInitialStateFromUrl(),
  ...getInitialLoginStatus(),
};

export const initialBookmarkState: IContextState = {
  ...initialState,
  ...getInitialBookmarkState(),
  ...getInitialLoginStatus(),
};
